import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { TextField } from '@/components/TextField';
import { PasswordInput } from '@/components/PasswordInput';
import { Checkbox } from '@/components/Checkbox';
import { Divider } from '@/components/Divider';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { signIn, signOut, AuthActionError } from '@/lib/auth-actions';
import { signInSchema, type SignInValues } from '@/lib/auth-schemas';
import { safeRedirectTarget } from '@/lib/safe-redirect';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { useRecaptchaToken } from '@/lib/useRecaptchaToken';
import { verifyRecaptcha } from '@/lib/recaptcha';
import { getProfile } from '@/lib/db/profile';
import { getSupabaseClient } from '@/lib/supabase';
import { env } from '@/lib/env';

export function SignInPage() {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  // Carry the post-login `redirect` (set by the protected-action auth gate) to
  // the sign-up link so a brand-new user also returns to the originating page.
  const redirectParam = params.get('redirect');
  const signupHref = redirectParam
    ? `/signup?redirect=${encodeURIComponent(redirectParam)}`
    : '/signup';

  // `enabled: !submitting` suppresses the redirect effect while the role
  // gate below is mid-flight. Without this, useSession's session update
  // after signIn() would race with our await getProfile() → signOut()
  // sequence and bounce the user to /dashboard before we have a chance
  // to reject a non-buyer profile.
  useAuthRedirect({
    mode: 'requireSignedOut',
    fallback: '/dashboard',
    enabled: !submitting,
  });

  // D1 — surface the AuthCallbackPage's collision redirect: an OAuth flow
  // landed on an existing email/password account and was rolled back.
  // The `id` argument dedupes if React fires the effect twice (StrictMode).
  useEffect(() => {
    if (params.get('error') === 'oauth_email_taken') {
      toast.error(t('auth:errors.oauthEmailTaken'), { id: 'oauth-email-taken' });
    }
  }, [params, t]);

  const getRecaptchaToken = useRecaptchaToken();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema(t)),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      // reCAPTCHA v3 (Spec V1.1 §M13.6). action='signin' — server-side
      // rate-limit is keyed on IP only (decision PO 2026-05-03, email
      // enumeration guard) so we deliberately omit `email` from the
      // verifyRecaptcha call.
      const token = await getRecaptchaToken('signin');
      if (!token && env.recaptchaSiteKey) throw new AuthActionError('recaptcha_failed');
      if (token) {
        const verdict = await verifyRecaptcha({ token, action: 'signin' });
        if (!verdict.success) {
          throw new AuthActionError(
            verdict.code === 'rate_limited' ? 'recaptcha_rate_limited' : 'recaptcha_failed',
          );
        }
      }
      await signIn(values);

      // Cross-entrypoint role gate. Supabase Auth only checks the password
      // against auth.users — it does not know that this entrypoint is
      // buyer-only. An admin or super_admin who submits valid credentials
      // here would otherwise sail through and hit a 403 on /dashboard
      // (RequireRole role="buyer"). We refetch the profile, sign out
      // anything that is not an active buyer, and surface the same generic
      // invalid_credentials error the wrong-password path uses — Spec V1.1
      // §M5 anti-enumeration policy forbids leaking that the email belongs
      // to a different role.
      const client = getSupabaseClient();
      const { data: sessionData } = await client.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) {
        await signOut(client).catch(() => undefined);
        setError('password', { message: t('auth:errors.invalidCredentials') });
        return;
      }

      const profile = await getProfile(userId, client);
      const isBuyer = profile?.role === 'buyer';
      const isActive = profile?.status === 'active' && profile?.deleted_at === null;

      if (!profile || !isBuyer || !isActive) {
        await signOut(client).catch(() => undefined);
        setError('password', { message: t('auth:errors.invalidCredentials') });
        return;
      }

      const target = safeRedirectTarget(params.get('redirect'), '/dashboard');
      navigate(target, { replace: true });
    } catch (error) {
      if (error instanceof AuthActionError) {
        if (error.code === 'recaptcha_failed') {
          toast.error(t('auth:errors.recaptchaFailed'));
        } else if (error.code === 'recaptcha_rate_limited') {
          toast.error(t('auth:errors.recaptchaRateLimited'));
        } else if (error.code === 'invalid_credentials') {
          // Spec V1.1 §M5 — generic error, attached to the password field.
          setError('password', { message: t('auth:errors.invalidCredentials') });
        } else if (error.code === 'rate_limited') {
          toast.error(t('auth:errors.rateLimited'));
        } else if (error.code === 'email_not_verified') {
          toast.error(t('auth:errors.emailNotVerified'));
        } else if (error.code === 'network') {
          toast.error(t('auth:errors.network'));
        } else {
          toast.error(t('auth:errors.generic'));
        }
      } else {
        toast.error(t('auth:errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <section className="m5-shell">
      <h1 className="m5-shell__title">{t('auth:signIn.title')}</h1>
      <p className="m5-shell__lead">{t('auth:signIn.lead')}</p>

      <div className="m5-card">
        <GoogleSignInButton />
        <Divider label={t('auth:common.or')} />

        <form className="m5-form" onSubmit={onSubmit} noValidate>
          <TextField
            label={t('auth:fields.email')}
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
          <PasswordInput
            label={t('auth:fields.password')}
            autoComplete="current-password"
            required
            error={errors.password?.message}
            showLabel={t('auth:common.passwordShow')}
            hideLabel={t('auth:common.passwordHide')}
            {...register('password')}
          />
          <div className="m5-form__row m5-form__row--end">
            <Link to="/reset-password" className="m5-link">
              {t('auth:signIn.forgotLink')}
            </Link>
          </div>
          <Checkbox label={t('auth:keepSignedIn.label')} name="keepSignedIn" />
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            loadingLabel={t('auth:common.loading')}
          >
            {t('auth:signIn.submit')}
          </Button>
        </form>

        <p className="m5-footnote">
          {t('auth:signIn.signupPrompt')}{' '}
          <Link to={signupHref} className="m5-link">
            {t('auth:signIn.signupLink')}
          </Link>
        </p>

        <p className="m5-recaptcha-notice">
          <Trans
            i18nKey="auth:common.recaptchaNotice"
            components={{
              privacy: (
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="m5-link"
                />
              ),
              terms: (
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="m5-link"
                />
              ),
            }}
          />
        </p>
      </div>
    </section>
  );
}
